import React, { useMemo } from 'react';
import { Box, Button, Card, CardContent, Paper, Stack, Typography } from '@mui/material';
import { getStageChipColor, getStageLabel, type WorkflowStage } from '../../utils/workflowLanes';

type LaneStageListProps = {
  title?: string;
  subtitle?: string;
  stages: WorkflowStage[];
  renderStageIcon?: (stage: WorkflowStage) => React.ReactNode;
  onNavigate: (path: string) => void;
};

export function LaneStageList({
  title = 'Commissioning stages',
  subtitle = 'Follow the commissioning story in order. Each stage routes to one canonical surface.',
  stages,
  renderStageIcon,
  onNavigate,
}: LaneStageListProps) {
  const currentStage = useMemo(
    () => stages.find((stage) => stage.status === 'next') || stages.find((stage) => stage.status === 'ready') || null,
    [stages],
  );
  const remainingStages = useMemo(
    () => stages.filter((stage) => stage.id !== currentStage?.id),
    [currentStage?.id, stages],
  );

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={2}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>

          {currentStage ? (
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderColor: currentStage.status === 'next' ? 'warning.main' : 'info.main',
                bgcolor: currentStage.status === 'next' ? 'rgba(237, 108, 2, 0.06)' : 'rgba(2, 136, 209, 0.06)',
              }}
            >
              <Stack spacing={1.25}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={2} flexWrap="wrap">
                  <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: '0.08em' }}>
                      Current stage
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1} sx={{ mt: 0.5, mb: 0.5 }}>
                      {renderStageIcon ? renderStageIcon(currentStage) : null}
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>
                        {currentStage.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color={getStageChipColor(currentStage.status) === 'warning' ? 'warning.main' : 'info.main'}
                        sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                      >
                        {getStageLabel(currentStage.status)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {currentStage.detail}
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color={currentStage.status === 'next' ? 'warning' : 'primary'}
                    onClick={() => onNavigate(currentStage.path)}
                  >
                    {`Open ${currentStage.label}`}
                  </Button>
                </Box>
              </Stack>
            </Paper>
          ) : null}

          <Stack spacing={1}>
            {remainingStages.map((stage) => {
              const isDone = stage.status === 'done';
              const isBlocked = stage.status === 'blocked';
              const isReady = stage.status === 'ready';
              return (
                <Paper
                  key={stage.id}
                  variant="outlined"
                  sx={{
                    p: 1.25,
                    bgcolor: isDone ? 'grey.50' : isReady ? 'rgba(2, 136, 209, 0.04)' : 'background.paper',
                    opacity: isBlocked ? 0.82 : 1,
                  }}
                >
                  <Box display="flex" justifyContent="space-between" gap={2} alignItems="center" flexWrap="wrap">
                    <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                      <Box display="flex" alignItems="center" gap={1} sx={{ mb: 0.25 }}>
                        {renderStageIcon ? renderStageIcon(stage) : null}
                        <Typography variant="subtitle2" sx={{ fontWeight: isDone ? 600 : 700 }}>
                          {stage.label}
                        </Typography>
                        <Typography
                          variant="caption"
                          color={
                            isDone
                              ? 'success.main'
                              : isReady
                                ? 'info.main'
                                : isBlocked
                                  ? 'text.secondary'
                                  : 'text.primary'
                          }
                          sx={{ fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}
                        >
                          {getStageLabel(stage.status)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        {stage.detail}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant={isDone ? 'text' : 'outlined'}
                      color={isReady ? 'info' : 'primary'}
                      onClick={() => onNavigate(stage.path)}
                    >
                      {isDone ? 'Review' : `Open ${stage.label}`}
                    </Button>
                  </Box>
                </Paper>
              );
            })}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}
